import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Workflow, Plus, Edit3, Trash2, Play, Pause, History,
  ShoppingCart, Sparkles, Heart, RefreshCcw, Calendar, MessagesSquare, Sparkle,
} from 'lucide-react';

const STATUS_BADGE = {
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const ARCHETYPE_ICONS = {
  abandoned_cart: ShoppingCart,
  festival_promo: Sparkles,
  welcome_drip: Heart,
  reorder_reminder: RefreshCcw,
  booking_followup: Calendar,
  enquiry_handoff: MessagesSquare,
};

function CreateGraphDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Seed a starter graph: trigger node placed at origin
      const res = await api.post('/api/flow-graphs', {
        name,
        description: '',
        graph: {
          nodes: [
            {
              id: 'trigger-1',
              type: 'trigger',
              position: { x: 80, y: 200 },
              data: { trigger_type: 'flow_complete', label: 'Flow completed' },
            },
          ],
          edges: [],
        },
      });
      toast.success('Graph created');
      onCreated?.(res.graph);
      setOpen(false);
      setName('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-graph-btn" className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2">
          <Plus className="w-4 h-4" /> New Flow
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Flow Graph</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="graph-name-input"
            placeholder="e.g. Aniyor puja-items routing"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="text-xs text-slate-400">
            We'll create the graph with a single Trigger node — drag in more nodes from the editor.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="graph-submit-btn" onClick={submit} disabled={submitting || !name}>
            {submitting ? 'Creating…' : 'Create & Open Editor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FlowGraphsListPage() {
  const [graphs, setGraphs] = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloning, setCloning] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [g, a] = await Promise.all([
        api.get('/api/flow-graphs'),
        api.get('/api/flow-graphs/gallery').catch(() => ({ archetypes: [] })),
      ]);
      setGraphs(g.graphs || []);
      setArchetypes(a.archetypes || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const cloneFromGallery = async (slug) => {
    setCloning(slug);
    try {
      const res = await api.post(`/api/flow-graphs/clone-from-gallery/${slug}`);
      toast.success(`Cloned "${res.graph.name}" — opening editor…`);
      navigate(`/dashboard/flow-builder/${res.graph.id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCloning(null);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete '${g.name}'? Run history will also be removed.`)) return;
    try {
      await api.del(`/api/flow-graphs/${g.id}`);
      toast.success('Graph deleted');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleStatus = async (g) => {
    const next = g.status === 'active' ? 'draft' : 'active';
    try {
      await api.put(`/api/flow-graphs/${g.id}`, { status: next });
      toast.success(next === 'active' ? 'Graph activated' : 'Graph paused');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-8" data-testid="flow-graphs-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Flow Builder</h2>
          <p className="text-sm text-slate-500 mt-1">
            Visual canvas for context-aware routing. Build, dry-run and activate per-vendor flows.
          </p>
        </div>
        <CreateGraphDialog onCreated={load} />
      </div>

      {/* ── Gallery section ── */}
      {archetypes.length > 0 && (
        <div data-testid="gallery-section">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-slate-700">Start from a template</h3>
            <span className="text-[11px] text-slate-400">— pre-built archetypes, one click to clone</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gallery-grid">
            {archetypes.map((a) => {
              const Icon = ARCHETYPE_ICONS[a.slug] || Workflow;
              return (
                <button
                  key={a.slug}
                  data-testid={`gallery-card-${a.slug}`}
                  onClick={() => cloneFromGallery(a.slug)}
                  disabled={cloning === a.slug}
                  className="text-left p-4 bg-white rounded-xl border border-gray-100 hover:border-[#00D1B2] hover:shadow-md transition disabled:opacity-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00D1B2]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[#00D1B2]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-slate-900">{a.name}</p>
                        <span className="text-[10px] uppercase tracking-wide text-slate-400">
                          {a.node_count} nodes
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {a.tags?.map((t) => (
                          <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase tracking-wide">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {cloning === a.slug && (
                    <p className="text-[10px] text-slate-400 mt-2 italic">Cloning…</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="flow-graphs-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : graphs.length === 0 ? (
          <div className="p-16 text-center">
            <Workflow className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-700">No flows yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Click "New Flow" to start building your first context-aware routing graph.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {graphs.map((g) => (
              <div
                key={g.id}
                data-testid={`graph-row-${g.id}`}
                className="flex items-start justify-between gap-4 p-5 hover:bg-slate-50/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm">{g.name}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_BADGE[g.status]}`}>
                      {g.status}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {g.graph?.nodes?.length ?? 0} nodes · v{g.version}
                    </span>
                  </div>
                  {g.description && <p className="text-xs text-slate-500 mt-1">{g.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Link to={`/dashboard/flow-builder/${g.id}/runs`}>
                    <Button variant="ghost" size="icon" data-testid={`graph-runs-${g.id}`}>
                      <History className="w-4 h-4 text-slate-500" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleStatus(g)}
                    data-testid={`graph-toggle-${g.id}`}
                  >
                    {g.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 text-emerald-600" />}
                  </Button>
                  <Link to={`/dashboard/flow-builder/${g.id}`}>
                    <Button variant="ghost" size="icon" data-testid={`graph-edit-${g.id}`}>
                      <Edit3 className="w-4 h-4 text-slate-500" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(g)}
                    data-testid={`graph-delete-${g.id}`}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
